import { useState, useCallback } from 'react'
import { VisioRoom } from '../visio/VisioRoom'
import { useDevicePreview } from './useDevicePreview'

interface GuestJoinData {
  token: string
  url: string
  room_name: string
  room_id: string
  is_guest: boolean
  role?: string
  permissions?: Record<string, boolean>
  channel_id?: string
}

export function GuestApp() {
  const [joinData, setJoinData] = useState<GuestJoinData | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const preview = useDevicePreview()

  const uuid = new URLSearchParams(window.location.search).get('uuid')

  const handleJoin = useCallback(async () => {
    if (!uuid) return
    const name = displayName.trim() || 'Invite'
    setLoading(true)
    setError('')
    try {
      const resp = await fetch(`/api/visio/guest/${uuid}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: name }),
      })
      if (!resp.ok) {
        const detail = await resp.text()
        throw new Error(detail || `Erreur ${resp.status}`)
      }
      const data = await resp.json()
      preview.stopPreview()
      setJoinData(data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Impossible de rejoindre')
    } finally {
      setLoading(false)
    }
  }, [uuid, displayName, preview])

  // No invite token
  if (!uuid) {
    return (
      <div className="h-screen flex items-center justify-center bg-zinc-950 text-zinc-300">
        <div className="text-center space-y-2">
          <h1 className="text-xl font-semibold">Lien invalide</h1>
          <p className="text-sm text-zinc-500">Aucun identifiant de reunion fourni.</p>
        </div>
      </div>
    )
  }

  // In conference
  if (joinData) {
    return (
      <div className="h-screen flex flex-col bg-zinc-950">
        <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
          <span className="text-sm text-zinc-400">Visio — {joinData.room_name || joinData.room_id.slice(0, 8)}</span>
          <button
            onClick={() => setJoinData(null)}
            className="px-4 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Se deconnecter
          </button>
        </div>
        <div className="flex-1 relative min-h-0 overflow-hidden">
          <VisioRoom
            token={joinData.token}
            serverUrl={joinData.url}
            onDisconnect={() => setJoinData(null)}
            roomMode="group"
            roomId={joinData.room_id}
            isGuest={true}
            role={joinData.role}
            permissions={joinData.permissions}
            channelId={joinData.channel_id}
          />
        </div>
      </div>
    )
  }

  // Join form with device preview
  return (
    <div className="h-screen flex items-center justify-center bg-zinc-950 text-zinc-200">
      <div className="w-full max-w-md p-6 space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-semibold">Rejoindre la reunion</h1>
          <p className="text-sm text-zinc-500">Verifiez votre camera et micro avant de participer</p>
        </div>

        {/* Video preview */}
        <div className="space-y-3">
          <div className="relative w-full aspect-video bg-zinc-900 rounded-lg overflow-hidden border border-zinc-700">
            {preview.permError ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-red-400 px-4 text-center">
                {preview.permError}
              </div>
            ) : (
              <video
                ref={preview.videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
            )}
          </div>

          {/* Device selectors */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Camera</label>
              <select
                value={preview.selectedVideoId}
                onChange={e => preview.setSelectedVideoId(e.target.value)}
                className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-300 focus:outline-none focus:border-blue-500"
              >
                {preview.cameras.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Camera ${d.deviceId.slice(0, 8)}...`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase mb-1">Micro</label>
              <select
                value={preview.selectedAudioId}
                onChange={e => preview.setSelectedAudioId(e.target.value)}
                className="w-full px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded text-xs text-zinc-300 focus:outline-none focus:border-blue-500"
              >
                {preview.mics.map(d => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Micro ${d.deviceId.slice(0, 8)}...`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Mic mute toggle */}
          <button
            onClick={preview.toggleMic}
            className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
              preview.micMuted
                ? 'bg-red-600/20 border border-red-600 text-red-300 hover:bg-red-600/30'
                : 'bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700'
            }`}
          >
            {preview.micMuted ? 'Micro coupe' : 'Micro actif'}
          </button>
        </div>

        <div className="space-y-3">
          <input
            type="text"
            placeholder="Votre nom"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleJoin()}
            autoFocus
            className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-blue-500"
          />

          <button
            onClick={handleJoin}
            disabled={loading}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
          >
            {loading ? 'Connexion...' : 'Rejoindre'}
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm text-center">
            {error}
          </div>
        )}
      </div>
    </div>
  )
}
